/**
 * Plain-English display names for landmark identifiers (coach/athlete-facing copy).
 *
 * Source of truth: `backend/app/services/landmark_utils.py`'s
 * `landmark_display_name()` / `_DISPLAY_SIDE_PREFIXES` /
 * `_DISPLAY_BASE_OVERRIDES` / `_VIRTUAL_DISPLAY_NAMES` / `_KNOWN_DISPLAY_BASES`.
 * This module mirrors that backend logic exactly so the frontend never has to
 * hand-roll a `.replace('_', ' ')` that leaks a raw token like "lead_knee" —
 * see docs/COACH_FACING_COPY_STANDARD.md §2 ("never: ... landmark identifiers").
 * If the backend map changes, update this file to match (see FE-8(a) in the
 * stop-hurting-today's-coaches PR).
 *
 * Deliberately does NOT resolve numeric index strings the way the backend
 * does ("16" -> "right wrist") — every frontend caller of this module passes
 * a named landmark (SuggestedGoal.body_parts, BodyPartEmphasis.landmark,
 * annotation.landmark_names), never a bare numeric index.
 *
 * Does NOT resolve sides. Callers pass the identifier they already have
 * (functional lead_/trail_ or physical left_/right_) — resolving a side here
 * would be a second, independent side-correction (the double-swap ban,
 * CLAUDE.md § Mirroring).
 */

/** Side prefixes a landmark identifier may carry. "lead"/"trail" are kept as
 * functional words — a coach says "lead wrist", not "front wrist" — never
 * flattened to left/right. */
const DISPLAY_SIDE_PREFIXES: ReadonlyArray<[string, string]> = [
  ['left', 'left'],
  ['right', 'right'],
  ['lead', 'lead'],
  ['trail', 'trail'],
];

/** Bases whose literal snake_case reads badly in a sentence. */
const DISPLAY_BASE_OVERRIDES: Readonly<Record<string, string>> = {
  foot_index: 'toes',
  eye_inner: 'eye',
  eye_outer: 'eye',
  pinky: 'little finger',
  index: 'index finger',
  mouth_left: 'mouth',
  mouth_right: 'mouth',
};

/** Virtual landmarks (not a single physical joint) get an explicit phrase. */
const VIRTUAL_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  mid_shoulder: 'shoulders',
  mid_hip: 'hips',
  mid_knee: 'knees',
  mid_ankle: 'ankles',
  mid_ear: 'ears',
  torso_center: 'torso',
  lead_mid_arm: 'lead upper arm',
  trail_mid_arm: 'trail upper arm',
  lead_mid_leg: 'lead thigh',
  trail_mid_leg: 'trail thigh',
};

/**
 * Every anatomical base the MediaPipe 33-landmark vocabulary contains, after
 * stripping a left_/right_ prefix (mirrors backend's `_KNOWN_DISPLAY_BASES`,
 * derived there from `LANDMARK_NAME_TO_INDEX`). Kept as an explicit literal
 * list here since the frontend has no equivalent index map to derive it from
 * — if the backend's 33-landmark vocabulary changes, update this list too.
 */
const KNOWN_DISPLAY_BASES: ReadonlySet<string> = new Set([
  'nose',
  'eye_inner',
  'eye',
  'eye_outer',
  'ear',
  'mouth_left',
  'mouth_right',
  'shoulder',
  'elbow',
  'wrist',
  'pinky',
  'index',
  'thumb',
  'hip',
  'knee',
  'ankle',
  'heel',
  'foot_index',
]);

/**
 * Turn a landmark identifier into plain English, or `null` if it is not one.
 *
 * `"left_knee"` -> `"left knee"`; `"lead_foot_index"` -> `"lead toes"`;
 * `"mid_hip"` -> `"hips"`; `"nose"` -> `"nose"`.
 *
 * Returns `null` — NEVER a snake_case fallback — for anything outside the
 * known vocabulary (a typo, a region label, an anchor sentinel). A caller
 * building coach-facing copy must then omit the phrase rather than print
 * internal vocabulary.
 */
export function landmarkDisplayName(name: string | null | undefined): string | null {
  if (!name) return null;
  const raw = name.trim().toLowerCase();
  if (!raw) return null;

  if (raw in VIRTUAL_DISPLAY_NAMES) {
    return VIRTUAL_DISPLAY_NAMES[raw];
  }

  let side: string | null = null;
  let base = raw;
  for (const [prefix, word] of DISPLAY_SIDE_PREFIXES) {
    if (raw.startsWith(`${prefix}_`)) {
      side = word;
      base = raw.slice(prefix.length + 1);
      break;
    }
  }

  let baseDisplay: string;
  if (base in DISPLAY_BASE_OVERRIDES) {
    baseDisplay = DISPLAY_BASE_OVERRIDES[base];
  } else if (KNOWN_DISPLAY_BASES.has(base)) {
    baseDisplay = base.replace(/_/g, ' ');
  } else {
    return null;
  }

  return side ? `${side} ${baseDisplay}` : baseDisplay;
}

/**
 * Convenience helper for a list of landmark identifiers (e.g.
 * `SuggestedGoal.body_parts`): resolves each through `landmarkDisplayName`,
 * drops any that don't resolve (never falls back to the raw token), and
 * returns the plain-English list. Returns `[]` when nothing resolved — the
 * caller should omit the whole phrase in that case rather than render an
 * empty "Body parts: " label.
 */
export function landmarkDisplayNames(names: readonly string[]): string[] {
  const out: string[] = [];
  for (const n of names) {
    const display = landmarkDisplayName(n);
    if (display !== null) out.push(display);
  }
  return out;
}
